import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Spinner } from '../../components/ui/spinner';
import { useToast } from '../../hooks/use-toast';
import { 
  ArrowLeft, 
  User, 
  Bike, 
  Plus, 
  Trash2, 
  Save,
  Edit,
  X
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auto-format registration number with dashes
const formatRegistrationNumber = (value) => {
  // Remove all non-alphanumeric characters
  const cleaned = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  
  // Check if it starts with letters (LA/HA format) or numbers (60-4240 format)
  const startsWithLetters = /^[A-Z]/.test(cleaned);
  
  if (startsWithLetters) {
    // Format: LA-60-4240 or HA-60-4240
    if (cleaned.length <= 2) {
      return cleaned;
    } else if (cleaned.length <= 4) {
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
    } else {
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 4)}-${cleaned.slice(4, 8)}`;
    }
  } else {
    // Format: 60-4240
    if (cleaned.length <= 2) {
      return cleaned;
    } else {
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 6)}`;
    }
  }
};

export default function CustomerEditPage() {
  const { id, garageSlug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    additional_phones: []
  });
  const [newPhone, setNewPhone] = useState('');
  const [bikes, setBikes] = useState([]);
  const [editingBikeId, setEditingBikeId] = useState(null);
  const [bikeForm, setBikeForm] = useState({
    make: '',
    model: '',
    year: '',
    registration_number: '',
    color: ''
  });
  const [showNewBike, setShowNewBike] = useState(false);
  const [deletingBikeId, setDeletingBikeId] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  const [bikeFormAttempted, setBikeFormAttempted] = useState(false);

  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';

  const validatePhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 11) {
      return language === 'en' ? 'Phone number must be 11 digits' : '\u09AB\u09CB\u09A8 \u09A8\u09AE\u09CD\u09AC\u09B0 \u0985\u09AC\u09B6\u09CD\u09AF\u0987 \u09E7\u09E7 \u09A1\u09BF\u099C\u09BF\u099F \u09B9\u09A4\u09C7 \u09B9\u09AC\u09C7';
    }
    if (!/^01/.test(digits)) {
      return language === 'en' ? 'Phone number must start with 01' : '\u09AB\u09CB\u09A8 \u09A8\u09AE\u09CD\u09AC\u09B0 01 \u09A6\u09BF\u09AF\u09BC\u09C7 \u09B6\u09C1\u09B0\u09C1 \u09B9\u09A4\u09C7 \u09B9\u09AC\u09C7';
    }
    return '';
  };

  useEffect(() => {
    fetchCustomerData();
  }, [id]);

  const fetchCustomerData = async () => {
    try {
      const [customerRes, bikesRes] = await Promise.all([
        axios.get(`${API}/customers/${id}`),
        axios.get(`${API}/bikes?customer_id=${id}`)
      ]);
      
      setFormData({
        name: customerRes.data.name,
        phone: customerRes.data.phone,
        email: customerRes.data.email || '',
        address: customerRes.data.address || '',
        notes: customerRes.data.notes || '',
        additional_phones: customerRes.data.additional_phones || []
      });
      setBikes(bikesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch customer:', error);
      toast({ 
        title: language === 'en' ? 'Failed to load customer' : 'গ্রাহক লোড ব্যর্থ',
        variant: 'destructive' 
      });
      navigate(`${basePath}/customers`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'phone') {
      setPhoneError(value ? validatePhone(value) : '');
    }
  };

  const handleBikeFormChange = (field, value) => {
    if (field === 'registration_number') {
      const formatted = formatRegistrationNumber(value);
      setBikeForm(prev => ({ ...prev, [field]: formatted }));
    } else {
      setBikeForm(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast({ 
        title: language === 'en' ? 'Name and phone are required' : 'নাম এবং ফোন প্রয়োজন',
        variant: 'destructive' 
      });
      return;
    }

    const phoneErr = validatePhone(formData.phone);
    if (phoneErr) {
      setPhoneError(phoneErr);
      toast({ title: phoneErr, variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/customers/${id}`, formData);
      toast({ title: language === 'en' ? 'Customer updated!' : 'গ্রাহক আপডেট হয়েছে!' });
      navigate(`${basePath}/customers/${id}`);
    } catch (error) {
      toast({ 
        title: language === 'en' ? 'Failed to update customer' : 'গ্রাহক আপডেট ব্যর্থ',
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  const startEditingBike = (bike) => {
    setEditingBikeId(bike.id);
    setBikeForm({
      make: bike.make,
      model: bike.model,
      year: bike.year || '',
      registration_number: bike.registration_number || '',
      color: bike.color || ''
    });
  };

  const cancelEditingBike = () => {
    setEditingBikeId(null);
    setBikeForm({ make: '', model: '', year: '', registration_number: '', color: '' });
  };

  const handleAddBike = async () => {
    if (!bikeForm.make || !bikeForm.model) {
      setBikeFormAttempted(true);
      toast({ 
        title: language === 'en' ? 'Make and model are required' : 'মেক এবং মডেল প্রয়োজন',
        variant: 'destructive' 
      });
      return;
    }
    setBikeFormAttempted(false);

    try {
      const bikeData = {
        customer_id: id,
        make: bikeForm.make,
        model: bikeForm.model,
        year: bikeForm.year ? parseInt(bikeForm.year) : null,
        registration_number: bikeForm.registration_number || null,
        color: bikeForm.color || null
      };
      
      const response = await axios.post(`${API}/bikes`, bikeData);
      setBikes(prev => [...prev, response.data]);
      setBikeForm({ make: '', model: '', year: '', registration_number: '', color: '' });
      setShowNewBike(false);
      toast({ title: language === 'en' ? 'Vehicle added!' : 'বাইক যোগ হয়েছে!' });
    } catch (error) {
      toast({ 
        title: language === 'en' ? 'Failed to add vehicle' : 'বাইক যোগ ব্যর্থ',
        variant: 'destructive' 
      });
    }
  };

  const handleUpdateBike = async (bikeId) => {
    if (!bikeForm.make || !bikeForm.model) {
      setBikeFormAttempted(true);
      toast({ 
        title: language === 'en' ? 'Make and model are required' : 'মেক এবং মডেল প্রয়োজন',
        variant: 'destructive' 
      });
      return;
    }
    setBikeFormAttempted(false);

    try {
      const bikeData = {
        customer_id: id,
        make: bikeForm.make,
        model: bikeForm.model,
        year: bikeForm.year ? parseInt(bikeForm.year) : null,
        registration_number: bikeForm.registration_number || null,
        color: bikeForm.color || null
      };
      
      const response = await axios.put(`${API}/bikes/${bikeId}`, bikeData);
      setBikes(prev => prev.map(b => b.id === bikeId ? response.data : b));
      setEditingBikeId(null);
      setBikeForm({ make: '', model: '', year: '', registration_number: '', color: '' });
      toast({ title: language === 'en' ? 'Vehicle updated!' : 'বাইক আপডেট হয়েছে!' });
    } catch (error) {
      toast({ 
        title: language === 'en' ? 'Failed to update vehicle' : 'বাইক আপডেট ব্যর্থ',
        variant: 'destructive' 
      });
    }
  };

  const handleDeleteBike = async (bikeId) => {
    try {
      await axios.delete(`${API}/bikes/${bikeId}`);
      setBikes(prev => prev.filter(b => b.id !== bikeId));
      setDeletingBikeId(null);
      toast({ title: language === 'en' ? 'Vehicle deleted!' : 'বাইক মুছে ফেলা হয়েছে!' });
    } catch (error) {
      toast({ 
        title: language === 'en' ? 'Failed to delete vehicle' : 'বাইক মুছে ফেলা ব্যর্থ',
        variant: 'destructive' 
      });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(`${basePath}/customers/${id}`)} 
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed']">
              {language === 'en' ? 'Edit Customer' : 'গ্রাহক সম্পাদনা'}
            </h1>
            <p className="text-slate-500">
              {language === 'en' ? 'Update customer information' : 'গ্রাহকের তথ্য আপডেট করুন'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {t('customer_details')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">{language === 'en' ? 'Full Name' : 'পুরো নাম'} *</Label>
                <Input
                  id="name"
                  placeholder={language === 'en' ? 'Enter customer name' : 'গ্রাহকের নাম লিখুন'}
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  className="h-12"
                  data-testid="customer-name"
                />
              </div>

              <div>
                <Label htmlFor="phone">{t('phone')} *</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="01XXXXXXXXX"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  required
                  maxLength={11}
                  className={`h-12 ${phoneError ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                  data-testid="customer-phone"
                />
                {phoneError && <p className="text-xs text-red-500 mt-1">{phoneError}</p>}
              </div>

              {/* Additional Phone Numbers */}
              <div>
                <Label>{language === 'en' ? 'Additional Numbers' : 'অতিরিক্ত নম্বর'}</Label>
                {formData.additional_phones.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 mt-1.5">
                    <Input value={p} readOnly className="h-10 bg-slate-50 flex-1" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        additional_phones: prev.additional_phones.filter((_, idx) => idx !== i)
                      }))}
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-2 mt-1.5">
                  <Input
                    type="tel"
                    placeholder={language === 'en' ? 'Add another number' : 'আরেকটি নম্বর যোগ করুন'}
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    className="h-10 flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-10"
                    disabled={!newPhone.trim() || formData.additional_phones.includes(newPhone.trim()) || newPhone.trim() === formData.phone}
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        additional_phones: [...prev.additional_phones, newPhone.trim()]
                      }));
                      setNewPhone('');
                    }}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="email">{t('email')} ({language === 'en' ? 'optional' : 'ঐচ্ছিক'})</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="customer@email.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="h-12"
                  data-testid="customer-email"
                />
              </div>

              <div>
                <Label htmlFor="address">{t('address')}</Label>
                <Textarea
                  id="address"
                  placeholder={language === 'en' ? 'Enter address' : 'ঠিকানা লিখুন'}
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  data-testid="customer-address"
                />
              </div>

              <div>
                <Label htmlFor="notes">{t('notes')}</Label>
                <Textarea
                  id="notes"
                  placeholder={language === 'en' ? 'Any additional notes...' : 'অতিরিক্ত নোট...'}
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  data-testid="customer-notes"
                />
              </div>
            </CardContent>
          </Card>

          {/* Bikes Management */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Bike className="w-5 h-5" />
                  {t('bikes_owned')}
                </CardTitle>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowNewBike(true);
                    setBikeForm({ make: '', model: '', year: '', registration_number: '', color: '' });
                  }}
                  data-testid="add-bike-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Add Vehicle' : 'বাইক যোগ করুন'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Existing Bikes */}
              {bikes.map((bike) => (
                <div key={bike.id} className="p-3 bg-slate-50 rounded-lg" data-testid={`bike-${bike.id}`}>
                  {editingBikeId === bike.id ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">{language === 'en' ? 'Make' : 'মেক'} *</Label>
                          <Input
                            placeholder="Yamaha, Honda..."
                            value={bikeForm.make}
                            onChange={(e) => handleBikeFormChange('make', e.target.value)}
                            className={`h-10 ${bikeFormAttempted && !bikeForm.make ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{language === 'en' ? 'Model' : 'মডেল'} *</Label>
                          <Input
                            placeholder="FZ-S, CBR..."
                            value={bikeForm.model}
                            onChange={(e) => handleBikeFormChange('model', e.target.value)}
                            className={`h-10 ${bikeFormAttempted && !bikeForm.model ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">{language === 'en' ? 'Year' : 'বছর'}</Label>
                          <Input
                            type="number"
                            placeholder="2024"
                            value={bikeForm.year}
                            onChange={(e) => handleBikeFormChange('year', e.target.value)}
                            className="h-10"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{language === 'en' ? 'Reg. Number' : 'রেজি. নম্বর'}</Label>
                          <Input
                            placeholder="LA-60-4240 or 60-4240"
                            value={bikeForm.registration_number}
                            onChange={(e) => handleBikeFormChange('registration_number', e.target.value)}
                            className="h-10"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">{language === 'en' ? 'Color' : 'রঙ'}</Label>
                          <Input
                            placeholder="Black"
                            value={bikeForm.color}
                            onChange={(e) => handleBikeFormChange('color', e.target.value)}
                            className="h-10"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleUpdateBike(bike.id)}
                          className="flex-1"
                        >
                          <Save className="w-3 h-3 mr-1" />
                          {language === 'en' ? 'Save' : 'সংরক্ষণ'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={cancelEditingBike}
                        >
                          <X className="w-3 h-3 mr-1" />
                          {language === 'en' ? 'Cancel' : 'বাতিল'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{bike.make} {bike.model}</p>
                        <div className="flex gap-2 text-sm text-slate-500">
                          {bike.year && <span>{bike.year}</span>}
                          {bike.registration_number && <span>• {bike.registration_number}</span>}
                          {bike.color && <span>• {bike.color}</span>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => startEditingBike(bike)}
                          data-testid={`edit-bike-${bike.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeletingBikeId(bike.id)}
                          data-testid={`delete-bike-${bike.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* New Bike Form */}
              {showNewBike && (
                <div className="p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">{language === 'en' ? 'Make' : 'মেক'} *</Label>
                        <Input
                          placeholder="Yamaha, Honda..."
                          value={bikeForm.make}
                          onChange={(e) => handleBikeFormChange('make', e.target.value)}
                          className={`h-10 ${bikeFormAttempted && !bikeForm.make ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{language === 'en' ? 'Model' : 'মডেল'} *</Label>
                        <Input
                          placeholder="FZ-S, CBR..."
                          value={bikeForm.model}
                          onChange={(e) => handleBikeFormChange('model', e.target.value)}
                          className={`h-10 ${bikeFormAttempted && !bikeForm.model ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">{language === 'en' ? 'Year' : 'বছর'}</Label>
                        <Input
                          type="number"
                          placeholder="2024"
                          value={bikeForm.year}
                          onChange={(e) => handleBikeFormChange('year', e.target.value)}
                          className="h-10"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{language === 'en' ? 'Reg. Number' : 'রেজি. নম্বর'}</Label>
                        <Input
                          placeholder="LA-60-4240 or 60-4240"
                          value={bikeForm.registration_number}
                          onChange={(e) => handleBikeFormChange('registration_number', e.target.value)}
                          className="h-10"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{language === 'en' ? 'Color' : 'রঙ'}</Label>
                        <Input
                          placeholder="Black"
                          value={bikeForm.color}
                          onChange={(e) => handleBikeFormChange('color', e.target.value)}
                          className="h-10"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleAddBike}
                        className="flex-1"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        {language === 'en' ? 'Add Vehicle' : 'বাইক যোগ করুন'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setShowNewBike(false);
                          setBikeForm({ make: '', model: '', year: '', registration_number: '', color: '' });
                        }}
                      >
                        <X className="w-3 h-3 mr-1" />
                        {language === 'en' ? 'Cancel' : 'বাতিল'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {bikes.length === 0 && !showNewBike && (
                <p className="text-slate-500 text-center py-4 text-sm">
                  {language === 'en' ? 'No vehicles registered' : 'কোন বাইক নিবন্ধিত নেই'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex gap-3">
            <Button 
              type="submit" 
              className="flex-1 h-12 bg-blue-800 hover:bg-blue-900 font-bold"
              disabled={saving}
              data-testid="save-customer"
            >
              {saving ? <Spinner size="sm" /> : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Save Changes' : 'পরিবর্তন সংরক্ষণ করুন'}
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-12"
              onClick={() => navigate(`${basePath}/customers/${id}`)}
              data-testid="cancel-edit"
            >
              {language === 'en' ? 'Cancel' : 'বাতিল'}
            </Button>
          </div>
        </form>
      </div>

      {/* Delete Bike Confirmation Dialog */}
      <AlertDialog open={!!deletingBikeId} onOpenChange={() => setDeletingBikeId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'en' ? 'Delete Vehicle?' : 'বাইক মুছবেন?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'en' 
                ? 'Are you sure you want to delete this vehicle? This action cannot be undone.' 
                : 'আপনি কি এই বাইকটি মুছে ফেলতে চান? এই কাজটি পূর্বাবস্থায় ফিরিয়ে আনা যাবে না।'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{language === 'en' ? 'Cancel' : 'বাতিল'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteBike(deletingBikeId)}
              className="bg-red-600 hover:bg-red-700"
            >
              {language === 'en' ? 'Delete' : 'মুছুন'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
